import forEach from 'lodash/forEach';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import isPlainObject from 'lodash/isPlainObject';
import { useId } from 'react';
import { FieldPath } from 'react-hook-form';

import { UseFormSetError } from 'react-hook-form/dist/types/form';
import { donationsPageValidationSelector } from 'src/pages/DonationSetup/containers/selectors';
import { store } from 'src/store/store';
import { BaseOptionType } from 'src/utils/commonForm';
import { getDOMElement, scrollIntoView } from 'src/utils/DOM';
import { PreparedFormErrorsType } from 'src/utils/errors';
import { SafeAnyType } from 'src/utils/safeAny';
import { StateFormSetError } from 'src/utils/stateForm';

/** sets id if not set, else returns existing id */
export const useIdOrRandomString = (id?: string | number): string => {
  const generatedId = useId();

  if (typeof id === 'number') {
    return id.toString();
  }
  return id || generatedId;
};

/** there need to be "?" for the older application */
export const normalizeFileUrl = (fileName: string): string => (/\?$/.test(fileName) ? fileName : `${fileName}?`);

// specific types are set. maybe safeAny?
/** check if a form is dirty (if values equal to initial values) */
export const isFormDirty = (initialForm: SafeAnyType | null, currentForm: SafeAnyType): boolean => {
  if (initialForm) {
    /** do not check if validating preview - it's already has to be saved */
    const validateMode = donationsPageValidationSelector(store.getState()).response;

    if (validateMode && validateMode === 'preview') {
      return false;
    }

    /** don't use inner variables starting with '_' */
    const baseFormArr = Object.entries(initialForm).filter(([key]) => !key.startsWith('_'));

    for (let i = 0; i < baseFormArr.length; i += 1) {
      const [key, value] = baseFormArr[i];
      const currentValue = currentForm[key];

      /** not object types (string, number, etc) */
      if (!isObject(value)) {
        if (currentValue !== undefined && value !== currentValue) {
          return true;
        }
        /** common used option type */
      } else if (get(value, 'singular', undefined) !== undefined && get(value, 'value', undefined) !== undefined) {
        type OptionType = BaseOptionType<number> & {
          singular: string | null;
          plural: string | null;
          article: string;
        };

        const initVal = value as OptionType;
        const curVal = currentValue as OptionType;

        if (initVal.value === null && initVal.value === curVal.value) {
          if (
            initVal.singular !== curVal.singular ||
            initVal.plural !== curVal.plural ||
            initVal.article !== curVal.article
          ) {
            return true;
          }
        } else if (get(value, 'value', undefined) !== get(currentValue, 'value', undefined)) {
          return true;
        }
        /** compare values if there is a key "value" and compare all properties */
      } else if (get(value, 'value') !== undefined && get(currentValue, 'value') !== undefined) {
        if (get(value, 'value') !== currentValue.value) {
          return !isEqual(value, currentValue);
        }
        /** compare objects by equality */
      } else if (isArray(value)) {
        if (!isEqual(value, currentValue)) {
          return true;
        }
      } else if (isPlainObject(value)) {
        /**
         * object may be partially equal to each other,
         * especially when the currentValue object doesn't contain all properties
         *
         * (not isEqual(value, currentValue))
         */

        let dirty = false;

        forEach(currentValue, (cValue, cKey) => {
          if ((value as Record<string, SafeAnyType>)[cKey] !== cValue) {
            dirty = true;
          }
        });

        return dirty;
      }
    }

    return false;
  }
  return false;
};

export const scrollToFirstFormError = (name: string): void => {
  scrollIntoView(getDOMElement(`[data-name="${name}"]`));
};

export const setHoverErrors = <T = Record<string, SafeAnyType>>(
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  setError: UseFormSetError<T> | StateFormSetError,
  errors: PreparedFormErrorsType,
  prefix = ''
): void => {
  errors.forEach(({ key, message }) =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setError((prefix ? `${prefix}${key}` : key) as FieldPath<T>, {
      type: 'hover',
      message,
    })
  );
};
